import React from "react";
import { useProductsSearchQuery } from "@queries/order"; // <— same pattern
import "./css/add-product-form.css";

function useDebounced(value, delay = 250) {
  const [v, setV] = React.useState(value);
  React.useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}

export default function AddProductForm({ onAdd }) {
  const [query, setQuery] = React.useState("");
  const [qty, setQty] = React.useState(1);
  const [open, setOpen] = React.useState(false);
  const [active, setActive] = React.useState(0);
  const [chosen, setChosen] = React.useState(null); // { value, label, ... }
  const [cooldown, setCooldown] = React.useState(0);

  const qtyRef = React.useRef(null);
  const searchRef = React.useRef(null);

  const dq = useDebounced(query, 250);
  const { data: options = [], isFetching } = useProductsSearchQuery(dq, 50);

  React.useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  React.useEffect(() => {
    if (open && chosen) setChosen(null);
  }, [query]); // keep clear when typing

  const selectAt = (i) => {
    const opt = options[i];
    if (!opt) return;
    setChosen(opt);
    setQuery(opt.label);
    setOpen(false);
    setTimeout(() => {
      if (qtyRef.current) {
        qtyRef.current.focus();
        qtyRef.current.select();
      }
    }, 0);
  };

  const handleKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, options.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      selectAt(active);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (cooldown > 0) return;

    const picked = chosen ?? options[0];
    if (!picked) return;
    const quantity = Math.max(1, Number(qty || 1));
    // <- calls your existing mutation wired in parent:
    // <AddProductForm onAdd={(payload)=>add.mutate(payload)} />
    onAdd?.({ product_id: picked.value, quantity });
    
    // start cooldown
    setCooldown(1);

    // reset
    setChosen(null);
    setQuery("");
    setQty(1);
    setOpen(false);
    setActive(0);
    setTimeout(() => searchRef.current?.focus(), 0);
  };

  return (
    <form className="apf" onSubmit={submit} autoComplete="off">
      <div className="apf-field">
        <label>Choose Product</label>
        <div className="apf-combo">
          <input
            ref={searchRef}
            className="apf-input"
            placeholder="type to search…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKey}
          />
          {open && (
            <ul className="apf-list" onMouseDown={(e) => e.preventDefault()}>
              {isFetching && <li className="apf-empty">Searching…</li>}
              {!isFetching && options.length === 0 && (
                <li className="apf-empty">No matches</li>
              )}
              {!isFetching && options.map((o, i) => (
                <li
                  key={o.value}
                  className={i === active ? "apf-item active" : "apf-item"}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => selectAt(i)}
                >
                  <div className="apf-line1">{o.label}</div>
                  <div className="apf-line2">
                    {o.model ? <span className="muted">{o.model}</span> : null}
                    {o.price != null ? <span className="price">{o.price.toFixed(2)}</span> : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="apf-field w-qty">
        <label>Quantity</label>
        <input
          ref={qtyRef}
          type="number"
          min={1}
          className="apf-input"
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          onFocus={(e) => e.target.select()}
        />
      </div>

      <div className="apf-actions">
        <button className="apf-btn" type="submit" disabled={isFetching || cooldown > 0}>
          {cooldown > 0 ? `Wait ${cooldown}s` : "Add Product"}
        </button>
      </div>
    </form>
  );
}

